import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { routesFor, type Lang } from "./primitives";

type Item = { name: string; path: string };

const NAV: Record<Lang, { services: Item[]; resources: Item[]; simple: Item[]; cta: string; tagline: string }> = {
  es: {
    services: [
      { name: "Todos los servicios", path: "/servicios" },
      { name: "Consultoría SEO", path: "/consultor-seo" },
      { name: "Auditoría SEO", path: "/servicios/seo-diagnostico" },
      { name: "SEO técnico", path: "/servicios/seo-tecnico" },
      { name: "SEO internacional", path: "/servicios/seo-internacional" },
      { name: "Mercado hispano (EE. UU.)", path: "/servicios/seo-bilingue-mercado-hispano" },
      { name: "Ecommerce SEO", path: "/servicios/ecommerce-seo" },
      { name: "Amazon SEO", path: "/servicios/amazon-consulting" },
      { name: "AI Search", path: "/servicios/posicionamiento-llm" },
      { name: "SEO local", path: "/seo-local" },
      { name: "Google Ads", path: "/servicios/google-ads" },
      { name: "Rediseño web", path: "/servicios/rediseno-web" },
      { name: "Branding", path: "/servicios/branding" },
    ],
    resources: [
      { name: "Biblioteca", path: "/biblioteca" },
      { name: "Blog", path: "/blog" },
      { name: "Herramientas", path: "/herramientas-seo" },
      { name: "Preguntas frecuentes", path: "/preguntas-frecuentes" },
    ],
    simple: [
      { name: "Casos", path: "/casos-de-exito" },
      { name: "Metodología", path: "/metodologia" },
      { name: "Sobre Luis ROC", path: "/sobre-mi" },
    ],
    cta: "Reunión inicial gratuita — 30 min",
    tagline: "Consultoría SEO y crecimiento digital",
  },
  en: {
    services: [
      { name: "All services", path: "/en/services" },
      { name: "SEO consulting", path: "/en/seo-consultant" },
      { name: "SEO audit", path: "/en/services/seo-audit" },
      { name: "Technical SEO", path: "/en/services/technical-seo" },
      { name: "International SEO", path: "/en/services/international-seo" },
      { name: "US Hispanic market SEO", path: "/en/services/hispanic-market-seo" },
      { name: "Ecommerce SEO", path: "/en/services/ecommerce-seo" },
      { name: "Amazon SEO", path: "/en/services/amazon-consulting" },
      { name: "AI Search", path: "/en/services/llm-positioning" },
      { name: "Local SEO", path: "/en/local-seo" },
      { name: "Google Ads", path: "/en/services/google-ads" },
      { name: "Website redesign", path: "/en/services/website-redesign" },
      { name: "Branding", path: "/en/services/branding" },
    ],
    resources: [
      { name: "Library", path: "/en/library" },
      { name: "Blog", path: "/en/blog" },
      { name: "Tools", path: "/en/seo-tools" },
      { name: "FAQ", path: "/en/faq" },
    ],
    simple: [
      { name: "Case studies", path: "/en/case-studies" },
      { name: "Methodology", path: "/en/methodology" },
      { name: "About Luis ROC", path: "/en/about" },
    ],
    cta: "Free initial consultation — 30 min",
    tagline: "SEO and digital growth consulting",
  },
};

const LABELS = {
  es: { services: "Servicios", resources: "Recursos", lang: "Idioma", nav: "Navegación principal" },
  en: { services: "Services", resources: "Resources", lang: "Language", nav: "Main navigation" },
};

/** Cabecera compartida del sitio en la dirección luminosa. */
export const SiteHeader = ({
  open,
  onOpenChange,
  language = "es",
  altUrl,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  language?: Lang;
  /** URL equivalente real en el otro idioma. */
  altUrl?: string;
}) => {
  const [scrolled, setScrolled] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const routes = routesFor(language);
  const nav = NAV[language];
  const l = LABELS[language];
  const other: Lang = language === "es" ? "en" : "es";
  const otherHref = altUrl ?? (language === "es" ? "/en" : "/");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Foco gestionado: Escape cierra, Tab queda contenido, el foco vuelve al disparador.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    panel?.querySelector<HTMLElement>("a, button")?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onOpenChange(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key !== "Tab" || !panel) return;
      const items = [
        triggerRef.current,
        ...Array.from(panel.querySelectorAll<HTMLElement>("a, button")),
      ].filter(Boolean) as HTMLElement[];
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const menuGroup = (label: string, items: Item[]) => (
    <DropdownMenu>
      <DropdownMenuTrigger className="v3-nav-link inline-flex items-center gap-1.5">
        {label}
        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[70vh] w-64 overflow-y-auto border-[color:var(--v3-line)] bg-white"
      >
        {items.map((item) => (
          <DropdownMenuItem key={item.path} asChild>
            <Link
              to={item.path}
              className="block px-3 py-2 text-[0.92rem] text-[color:var(--v3-ink-soft)]"
            >
              {item.name}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled || open ? "bg-[color:var(--v3-mineral)] backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="v3-shell">
        <div className="flex h-20 items-center justify-between gap-6 lg:h-24">
          <Link
            to={routes.home}
            className="flex items-center gap-3"
            aria-label={language === "es" ? "Luis ROC — inicio" : "Luis ROC — home"}
          >
            <img
              src="/media/luis-roc/site-mark.png"
              alt=""
              width={36}
              height={36}
              className="h-8 w-auto"
            />
            <span className="flex flex-col leading-none">
              <span className="text-[1.15rem] font-extrabold tracking-[-0.03em] text-[color:var(--v3-ink)]">
                Luis ROC
              </span>
              <span className="v3-micro mt-1 hidden max-w-[16rem] text-[0.6rem] sm:block">{nav.tagline}</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-6 whitespace-nowrap lg:flex" aria-label={l.nav}>
            {menuGroup(l.services, nav.services)}
            {nav.simple.map((item) => (
              <Link key={item.path} to={item.path} className="v3-nav-link">
                {item.name}
              </Link>
            ))}
            {menuGroup(l.resources, nav.resources)}

            <span aria-hidden className="h-5 w-px bg-[color:var(--v3-line)]" />
            <span className="flex items-center gap-2 text-sm" role="group" aria-label={l.lang}>
              <span className="font-semibold text-[color:var(--v3-blue)]" aria-current="true">
                {language.toUpperCase()}
              </span>
              <span aria-hidden className="text-[color:var(--v3-muted)]">
                /
              </span>
              <Link to={otherHref} className="v3-nav-link text-[color:var(--v3-muted)]">
                {other.toUpperCase()}
              </Link>
            </span>

            <Link to={routes.seoDiagnosis} className="v3-btn h-11 min-h-[2.75rem] whitespace-nowrap text-[0.9rem]">
              {nav.cta}
            </Link>
          </nav>

          <button
            ref={triggerRef}
            type="button"
            onClick={() => onOpenChange(!open)}
            aria-expanded={open}
            aria-controls="v3-mobile-nav"
            className="flex h-11 w-11 items-center justify-center text-[color:var(--v3-ink)] lg:hidden"
            aria-label={
              open
                ? language === "es"
                  ? "Cerrar menú"
                  : "Close menu"
                : language === "es"
                  ? "Abrir menú"
                  : "Open menu"
            }
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <nav
            ref={panelRef}
            id="v3-mobile-nav"
            aria-label={language === "es" ? "Navegación móvil" : "Mobile navigation"}
            className="max-h-[75vh] overflow-y-auto border-t border-[color:var(--v3-line)] pb-6 lg:hidden"
          >
            {[...nav.simple].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => onOpenChange(false)}
                className="block border-b border-[color:var(--v3-line)] py-4 text-lg font-semibold text-[color:var(--v3-ink)]"
              >
                {item.name}
              </Link>
            ))}

            <p className="v3-micro mt-6">{l.services}</p>
            <ul className="mt-2">
              {nav.services.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => onOpenChange(false)}
                    className="block py-2.5 text-[0.97rem] text-[color:var(--v3-ink-soft)]"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>

            <p className="v3-micro mt-6">{l.resources}</p>
            <ul className="mt-2">
              {nav.resources.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => onOpenChange(false)}
                    className="block py-2.5 text-[0.97rem] text-[color:var(--v3-ink-soft)]"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex items-center gap-3 text-sm" role="group" aria-label={l.lang}>
              <span className="font-semibold text-[color:var(--v3-blue)]">
                {language.toUpperCase()}
              </span>
              <span aria-hidden className="text-[color:var(--v3-muted)]">
                /
              </span>
              <Link to={otherHref} className="py-2 text-[color:var(--v3-muted)]">
                {other.toUpperCase()}
              </Link>
            </div>

            <div className="mt-6 grid gap-3">
              <Link
                to={routes.seoDiagnosis}
                onClick={() => onOpenChange(false)}
                className="v3-btn h-12 w-full justify-center"
              >
                {nav.cta}
              </Link>
              <a
                href="tel:+18325215050"
                onClick={() => trackEvent("phone_click", { location: "mobile_menu" })}
                className="v3-btn-ghost h-12 w-full justify-center"
              >
                <Phone className="h-4 w-4" aria-hidden />
                +1 (832) 521-5050
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
